import { Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { Calendar } from "../../../Icon/icons/calendar"
import { homeCardStyle, homeColor, homeText } from "../home-theme.ts"
import type { UpcomingMilestoneRow } from "./homeTodayCounts.ts"

/** The mockup shows three rows and a View all. */
const PREVIEW_ROWS = 3

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC"
]

/**
 * A bare `YYYY-MM-DD` read as a calendar day — never through `new Date()`,
 * which would place it at UTC midnight and a day early west of Greenwich.
 */
const dayParts = (iso: string): { month: string; day: string } | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  return { month: MONTHS[Number(m[2]) - 1] ?? "", day: String(Number(m[3])) }
}

const daysUntil = (iso: string, today: string): number | null => {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(today)
  if (!a || !b) return null
  const toDays = (p: RegExpExecArray) =>
    Date.UTC(Number(p[1]), Number(p[2]) - 1, Number(p[3])) / 86_400_000
  return toDays(a) - toDays(b)
}

const CardShell = ({
  icon,
  title,
  action,
  children
}: React.PropsWithChildren<{
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
}>) => (
  <section style={{ ...homeCardStyle, overflow: "hidden", minWidth: 0 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderBottom: `1px solid ${homeColor.border}`
      }}
    >
      <span
        aria-hidden
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: homeColor.tealPale,
          color: homeColor.teal,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {icon}
      </span>
      {/* `textAlign` is explicit: a shell-level rule centres bare headings,
          which is what put these titles in the middle of the card header. */}
      <h3
        style={{
          ...homeText.itemTitle,
          color: homeColor.ink,
          margin: 0,
          flex: 1,
          textAlign: "start"
        }}
      >
        {title}
      </h3>
      {action}
    </div>
    {children}
  </section>
)

/**
 * §7's Upcoming milestones card, drawn from the same `upcoming_milestones`
 * rows the Today band counts — one source, so the "3 this week" figure and
 * the rows under it can never disagree.
 */
export const HomeMilestonesCard = ({
  rows,
  today
}: {
  rows: UpcomingMilestoneRow[] | undefined
  today: string
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const preview = (rows ?? []).slice(0, PREVIEW_ROWS)

  return (
    <CardShell
      icon={<Calendar width={14} height={14} />}
      title={t("home.schedule.milestones", "Upcoming milestones")}
      action={
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => navigate("/projects")}
        >
          {t("common.viewAll", "View all")}
        </Button>
      }
    >
      {rows === undefined ? (
        <p
          style={{
            ...homeText.meta,
            color: homeColor.inkMuted,
            margin: 0,
            padding: 16
          }}
        >
          {t(
            "home.section.widgetMissing",
            "Add this widget from Edit layout to show it here."
          )}
        </p>
      ) : preview.length === 0 ? (
        <p
          style={{
            ...homeText.meta,
            color: homeColor.inkMuted,
            margin: 0,
            padding: 16
          }}
        >
          {t("home.schedule.noMilestones", "No upcoming milestones.")}
        </p>
      ) : (
        preview.map((row, index) => {
          const parts = row.end_date ? dayParts(row.end_date) : null
          const due = row.end_date ? daysUntil(row.end_date, today) : null
          const overdue = due != null && due < 0
          return (
            <div
              key={`${row.project}-${row.phase}-${index}`}
              role={row.drill_to ? "button" : undefined}
              tabIndex={row.drill_to ? 0 : undefined}
              onClick={row.drill_to ? () => navigate(row.drill_to!) : undefined}
              onKeyDown={
                row.drill_to
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        navigate(row.drill_to!)
                      }
                    }
                  : undefined
              }
              className="home-schedule__row"
            >
              <span className="home-schedule__date" aria-hidden={!parts}>
                {parts ? (
                  <>
                    <span
                      style={{ ...homeText.chip, color: homeColor.inkMuted }}
                    >
                      {parts.month}
                    </span>
                    <span
                      style={{ ...homeText.itemTitle, color: homeColor.ink }}
                    >
                      {parts.day}
                    </span>
                  </>
                ) : (
                  <span style={{ ...homeText.chip, color: homeColor.inkMuted }}>
                    —
                  </span>
                )}
              </span>
              <span
                className="min-w-0"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
                <span
                  style={{ ...homeText.actionTitle, color: homeColor.ink }}
                  className="truncate"
                >
                  {row.phase ?? ""}
                </span>
                <span
                  style={{ ...homeText.meta, color: homeColor.inkMuted }}
                  className="truncate"
                >
                  {row.project ?? ""}
                </span>
              </span>
              {due != null ? (
                <span
                  style={{
                    ...homeText.chip,
                    padding: "3px 10px",
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                    color: overdue ? homeColor.delayedInk : homeColor.atRiskInk,
                    background: overdue
                      ? homeColor.criticalBg
                      : homeColor.warningBg
                  }}
                >
                  {overdue
                    ? t("home.schedule.overdueBy", "{{count}} days overdue", {
                        count: -due
                      })
                    : due === 0
                      ? t("home.schedule.dueToday", "Today")
                      : t("home.schedule.dueIn", "{{count}} days", {
                          count: due
                        })}
                </span>
              ) : null}
            </div>
          )
        })
      )}
    </CardShell>
  )
}
